import React from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { ErrorMessage } from '@hookform/error-message';
import * as yup from 'yup';
import cx from 'classnames';

import withApollo from 'components/withApollo';
import PageLayout from 'components/PageLayout';
import Hero from 'components/Hero';
import EditorContent from 'components/EditorContent';
import TitleGroup from 'components/TitleGroup';
import Flex from 'components/Flex';
import FormLabel from 'components/FormLabel';
import FormInput from 'components/FormInput';
import FormTextarea from 'components/FormTextarea';
import FormError from 'components/FormError';
import Button from 'components/Button';
import Link from 'components/Link';

const QUERY = gql`
  query {
    allCateringPages {
      id
      leadIn
      title
      content {
        document
      }
      heroImage {
        url: publicUrlTransformed(
          transformation: {
            width: "1440"
            height: "720"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }
      quoteLeadIn
      quoteTitle
      cateringMenu {
        url: publicUrl
      }
    }
  }
`;

const CREATE_REQUEST = gql`
  mutation CreateRequest($data: CateringQuoteRequestCreateInput!) {
    createCateringQuoteRequest(data: $data) {
      id
    }
  }
`;

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  description: yup.string().required('Description is required'),
});

export default withApollo(() => {
  const { data } = useQuery(QUERY);
  const [createQuoteRequest, { error }] = useMutation(CREATE_REQUEST);

  const { register, errors, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
  });

  const { isSubmitting, isSubmitSuccessful } = formState;

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        await createQuoteRequest({ variables: { data: values } });
      } catch (err) {
        console.error('Error creating quote request', err);
      }
    },
    [createQuoteRequest]
  );

  const page = React.useMemo(() => data?.allCateringPages?.[0], [data]);
  if (!page) return null;

  return (
    <PageLayout title={page.title}>
      <Hero leadIn={page.leadIn} title={page.title} image={page.heroImage?.url} />
      <div className="my-16">
        <div className="container max-w-6xl">
          {page.content?.document && (
            <EditorContent
              component="div"
              className="text-center text-white mt-8 text-lg leading-loose max-w-3xl mx-auto rich-text"
              content={page.content}
            />
          )}
          {page.cateringMenu?.url && (
            <div className="mt-8 text-center text-lg">
              <Link
                href={page.cateringMenu?.url}
                className="text-accent hover:text-accent-light transition-colors duration-200"
                external
              >
                Download catering menu (PDF)
                <FontAwesomeIcon icon={faChevronRight} size="1x" className="ml-3" />
              </Link>
            </div>
          )}
          <div className="lg:flex items-start -ml-16 mt-12">
            <div className="pl-16 pt-8 w-full lg:w-2/5">
              <TitleGroup leadIn={page.quoteLeadIn} title={page.quoteTitle} />
            </div>
            <div className="pl-16 pt-8 w-full lg:w-3/5">
              <form
                className="bg-gray-darker p-8 rounded-lg max-w-xl mx-auto"
                onSubmit={handleSubmit(onSubmit)}
              >
                {isSubmitSuccessful ? (
                  <div className="lg:py-8">
                    <p className="text-center text-xl text-accent">Thanks!</p>
                    <p className="text-center text-lg text-white">
                      We&apos;ll be in touch shortly.
                    </p>
                  </div>
                ) : (
                  <Flex container="-mt-6" item="pt-6 w-full">
                    <div>
                      <FormLabel htmlFor="name">Your name</FormLabel>
                      <FormInput type="text" name="name" ref={register} />
                      <ErrorMessage errors={errors} name="name" render={FormError} />
                    </div>
                    <div>
                      <FormLabel htmlFor="email">Email address</FormLabel>
                      <FormInput type="email" name="email" ref={register} />
                      <ErrorMessage errors={errors} name="email" render={FormError} />
                    </div>
                    <div>
                      <FormLabel htmlFor="email">Phone number</FormLabel>
                      <FormInput type="text" name="phone" ref={register} />
                      <ErrorMessage errors={errors} name="phone" render={FormError} />
                    </div>
                    <div>
                      <FormLabel htmlFor="description">Description of Event</FormLabel>
                      <FormTextarea name="description" ref={register} />
                      <ErrorMessage errors={errors} name="description" render={FormError} />
                    </div>
                    <div className="text-center">
                      {error && (
                        <p className="mb-4">
                          There was an issue sending your request. Please try again later.
                        </p>
                      )}
                      <Button
                        component="button"
                        type="submit"
                        className={cx('w-full lg:w-auto transition-opacity duration-200', {
                          'opacity-50 pointer-events-none': isSubmitting,
                        })}
                      >
                        {isSubmitting ? 'Sending...' : 'Send request'}
                      </Button>
                    </div>
                  </Flex>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
});
